import { useNavigate } from 'react-router-dom';
import { getActiveClinicRef } from '../lib/clinicSignal.js';

// Maps old screen-name strings to URL paths.
// Preserves the existing navigate(screenName, data) call-site API so no
// .web.jsx / .mobile.jsx files need to change.
//
// A name missing here makes navigate() a silent no-op (see the `if (!path) return`
// below), so a sidebar item whose id is absent simply does nothing when clicked —
// no error, no route change. Exported so a test can assert every nav item resolves.
//
// ── CLINIC PREFIXING ────────────────────────────────────────────────────────────
// Every clinic-scoped path is served under `/c/:clinicRef/…`, because the active
// clinic is the URL and not a stored value (docs/CLINIC_MULTI_CLINIC_UX_REV2.md
// §A1). That is what makes two tabs on two clinics work with no coordination, and
// what makes a «پرداخت‌ها» bookmark saved at the Isfahan branch open the Isfahan
// branch.
//
// The prefix is applied HERE, in the one place paths are built, rather than at
// ~200 call sites. `ROUTES` therefore keeps returning bare paths and every existing
// `navigate('reception')` keeps working unchanged.
/**
 * Append a query string to a bare route path.
 *
 * Every builder below that takes one is answering the same need: a link from a
 * REPORT has to arrive somewhere already narrowed. «۳ ادعای ارسال‌نشده» that lands on
 * the whole claims ledger has made the reader start the search again, which is the
 * difference between a link that answers a question and one that merely changes
 * page. Undefined and empty values are dropped so a caller can pass a partial
 * object without producing `?insurer=undefined`.
 *
 * `params` is defaulted with `|| {}` rather than a parameter default, because
 * `useAppNavigate` passes `data = null` — and a parameter default only fires for
 * `undefined`. So every no-argument `navigate('reception')` arrived here as an
 * explicit null and threw on `Object.entries(null)`. Tests call the builders as
 * `resolve()`, which DOES trigger the parameter default, so the crash was
 * invisible to them.
 */
const withQuery = (path, params) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
};

export const ROUTES = {
  'reception': () => '/reception',
  'patients': () => '/patients',
  // Prefill: «ثبت بیمار جدید با این کد ملی» from the no-match state, and the
  // placeholder label a «ثبت سریع» row was held on (redesign §10.1/§10.2). Nothing
  // is reserved by prefilling — the mobile lookup on the form, and `addPatient`'s own
  // PHONE_HAS_PATIENTS check, are what stop a second record for the same person.
  'add-patient': (data) => {
    const q = new URLSearchParams();
    if (data?.nid) q.set('nid', data.nid);
    if (data?.phone) q.set('phone', data.phone);
    if (data?.name) q.set('name', data.name);
    const search = q.toString();
    return search ? `/patients/new?${search}` : '/patients/new';
  },
  // The deep link INTO a patient's record. Reception and «حسابداری» both know
  // a patient and need somewhere to send the operator; before this the only way in
  // was to find the row by hand (redesign finding F4, §16.4).
  'patient': (data) => {
    const q = new URLSearchParams();
    if (data?.id) q.set('patient', data.id);
    if (data?.tab) q.set('tab', data.tab);
    if (data?.compose) q.set('compose', data.compose);
    if (data?.appointment) q.set('appointment', data.appointment);
    const search = q.toString();
    return search ? `/patients?${search}` : '/patients';
  },
  // The patient's CLINICAL record — «سوابق بیمار» — and the visit room: the shared
  // clinical module, rendered inside this panel for a practitioner of the clinic
  // (docs/CLINICAL_KIT_DESIGN.md). Not the same thing as 'patient' above, which
  // is this panel's own record drawer (identity, money, contact).
  'patient-record': (data) => {
    const id = data?.id || data?.patientId;
    // No patient, no record to open: the registry is the honest landing.
    if (!id) return '/patients';
    const tab = data?.tab && data.tab !== 'overview' ? `/${encodeURIComponent(data.tab)}` : '';
    return `/patients/${encodeURIComponent(id)}/record${tab}`;
  },
  'visit': (data) => {
    const id = data?.id || data?.patientId;
    // No patient, no visit: پذیرش is where one is started from.
    return id ? `/visit/${encodeURIComponent(id)}` : '/reception';
  },
  // «تقویم» was removed from the panel on 2026-09-13. The name stays mapped at
  // پذیرش — the desk's screen — so no existing navigate('calendar') call site
  // becomes a silent no-op.
  'calendar': () => '/reception',
  // A deep link names the report it wants. Callers pass `{ view: 'receivables' }`
  // and land on that report rather than on the overview, which is the difference
  // between a link that answers a question and one that starts a search.
  'reports': (data) => (data?.view ? `/reports?view=${encodeURIComponent(data.view)}` : '/reports'),
  // ── The two halves of the old «عملیات و هزینه» ────────────────────────────
  //
  // «مدیریت هزینه‌ها» kept `/operations`; «کالاها و خدمات» is `/goods`. Every old
  // NAME stays mapped and now points at whichever page absorbed it, so no
  // existing `navigate('inventory')` call site becomes a silent no-op and none
  // of them had to learn the new path — which is the whole reason paths are
  // built here and not at ~200 call sites.
  'operations': (data) => withQuery('/operations', data),
  'goods': (data) => withQuery('/goods', data),
  'inventory': (data) => withQuery('/goods', { view: 'items', ...(data || {}) }),
  // The equipment register — the dashboard's alert tile lands on it.
  'equipment': (data) => withQuery('/goods', { view: 'equipment', ...(data || {}) }),
  'credits': () => '/credits',
  // «پزشکان» merged into «همکاران» — the old name stays mapped and lands
  // on the practitioner half, so no call site becomes a silent no-op.
  'doctors': () => '/people?kind=practitioner',
  'register-doctor': () => '/register-doctor',
  'login': () => '/login',
  // «حسابداری» — the Financial Operations hub.
  'accounting': (data) => withQuery('/accounting', data),
  // «فروشگاه» — the clinic store. `{ view, listing, order, kind, tab }` narrow
  // it: the goods page's «در فروشگاه» chip lands on the listing it named.
  'store': (data) => withQuery('/store', data),
  // The old «پرداخت‌ها» NAME stays mapped and now builds the hub path, so every
  // existing `navigate('payments', { patient, range })` call site — the patient
  // drawer's «دفتر پرداخت» and two report drill-downs — follows the move without
  // being edited. `view=claims`, the one value that named the OTHER
  // ledger on that route, lands on the tab that ledger became; everything else is
  // the receipts view, with the caller's narrowing carried through verbatim.
  'payments': (data) => {
    const { view, ...rest } = data || {};
    if (view === 'claims') {
      return withQuery('/accounting', { view: 'receivables', tab: 'insurers', ...rest });
    }
    return withQuery('/accounting', { view: 'receipts', ...rest });
  },
  'claims': (data) => withQuery('/accounting', { view: 'receivables', tab: 'insurers', ...(data || {}) }),
  // Both take `data`, so a report's drill-down can carry its narrowing into the
  // URL instead of dropping it on the floor: `ROUTES.expenses` used to take no
  // argument at all, which is why «هزینه‌های عملیاتی، مرکز پذیرش» landed on the
  // unfiltered ledger. Whether each view READS its parameters is a separate,
  // smaller gap (split design §12 D9) — this is the half that belongs here.
  'expenses': (data) => withQuery('/operations', { view: 'expenses', ...(data || {}) }),
  'services': (data) => withQuery('/goods', { view: 'services', ...(data || {}) }),
  'people': () => '/people',
  // «حقوق و دستمزد» — split in two on 2026-09-13, and both halves keep a NAME
  // here so no existing call site becomes a silent no-op. The bare `payroll`
  // still means «take me to the payroll», which is now the OPERATION: the tab of
  // «همکاران» where a month is calculated, corrected, finalized and paid — and it
  // is what «بدهی‌ها → حقوق» and the employee counterparty drawer have always
  // meant by it. `payroll-report` is the accountant's reading of the same month
  // in «حسابداری». Both carry `{ y, m }` when the caller knows the month, so a
  // link opens the one the reader was looking at rather than today's.
  'payroll': (data) => withQuery('/people', { tab: 'payroll', ...(data || {}) }),
  'payroll-report': (data) => withQuery('/accounting', { view: 'payroll', ...(data || {}) }),
  // The two objects «همکاران» holds. The roles tab is a real destination — the
  // role editor's back affordance points at it, and landing that reader on a
  // table of people is a step they then have to undo.
  'people-roles': () => '/people?tab=roles',
  // «who holds this role» — the crossing from a role to the people who have it,
  // which is the question a role is usually opened to ask.
  'people-by-role': (roleId) => `/people?kind=staff&role=${encodeURIComponent(roleId || '')}`,
  // «اشتراک کلینیک» and «معرفی کلینیک» merged into «کلینیک من». Both old names
  // stay mapped and land on the view that absorbed them, so every existing
  // navigate('subscription') call site — the shell's paywall banner among them —
  // keeps working instead of becoming a silent no-op.
  // `view` lands the caller on a tab. Without it the «+» beside the قالب picker in
  // «ارسال پیام» opened «کلینیک من» on مشخصات — the page's default — instead of
  // «قالب‌های پیام», which is the only reason anyone presses it.
  'clinic': (data) => (data?.view ? `/clinic?view=${encodeURIComponent(data.view)}` : '/clinic'),
  'about': () => '/clinic?view=connect',
  'subscription': () => '/clinic?view=billing',
  // The WhatsApp link lives here, and messaging cannot work until a clinic has
  // paired its number — so the compose dialog and the «اتصال قطع شده» alert both
  // need a real destination to send the operator to.
  'settings': (data) => (data?.section ? `/settings?section=${data.section}` : '/settings'),
};

/**
 * Routes that belong to the PERSON, not to a clinic, and are therefore never
 * prefixed.
 *
 * A person reaches «شروع» and «دعوت‌ها» precisely when they may have no clinic at
 * all, so prefixing them would produce `/c//invitations` — a path that cannot
 * resolve, at the exact moment the person has nowhere else to go.
 */
export const PERSON_ROUTES = {
  'start': () => '/start',
  'invitations': () => '/invitations',
  'onboarding': () => '/onboarding',
};

/** Prefix a bare path with the active clinic. Query strings survive intact. */
export function withClinic(path, ref = getActiveClinicRef()) {
  if (!path || !ref) return path;
  if (path.startsWith('/c/')) return path;
  return `/c/${ref}${path}`;
}

/** Strip `/c/:ref` from a pathname, for prefix matching against bare paths. */
/**
 * The clinic ref a path names, or '' — the exact inverse of `withClinic`'s prefix.
 *
 * `ClinicContextProvider` needs this because it is mounted ABOVE the route that
 * declares `:clinicRef` (it has to wrap `/start` and `/onboarding` too), and
 * `useParams()` only reports the params of routes matched at or above the calling
 * component — so the provider always read `undefined` there. The pathname is the
 * one thing that is true wherever it is read from.
 */
export function clinicRefOfPath(pathname = '') {
  const m = /^\/c\/([^/]+)/.exec(pathname || '');
  if (!m) return '';
  try {
    return decodeURIComponent(m[1]);
  } catch {
    // A ref that is not valid percent-encoding is not a ref we could match anyway.
    return m[1];
  }
}

export function stripClinic(pathname = '') {
  const m = /^\/c\/[^/]+(\/.*)?$/.exec(pathname);
  if (!m) return pathname || '/';
  return m[1] || '/';
}

/**
 * `opts.replace` replaces the current history entry instead of pushing one.
 *
 * It exists for ONE caller shape: an overlay that is closing in order to send
 * the operator somewhere. Such an overlay holds a history entry of its own, and
 * a push would stack the destination ON TOP of it — leaving a phantom entry
 * that back lands on, and racing the overlay's own release (see
 * `mobile/useOverlayHistory.js`, `consumeOverlayEntry`). Replacing puts the
 * destination in the overlay's slot, so one back press returns to the page the
 * overlay was opened from.
 *
 * Ordinary navigation must NOT pass it: replacing there would make back skip
 * the section the operator came from.
 */
export function useAppNavigate() {
  const rrNavigate = useNavigate();
  return (to, data = null, opts = null) => {
    const nav = {
      ...(data ? { state: data } : {}),
      ...(opts?.replace ? { replace: true } : {}),
    };
    const options = Object.keys(nav).length ? nav : undefined;
    const personPath = PERSON_ROUTES[to]?.();
    if (personPath) {
      rrNavigate(personPath, options);
      window.scrollTo(0, 0);
      return;
    }
    const path = ROUTES[to]?.(data);
    if (!path) return;
    rrNavigate(withClinic(path), options);
    window.scrollTo(0, 0);
  };
}
